import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import { useCallback, useMemo, useRef } from 'react';
import { DateRangeValue } from 'src/components/DateRange/date-range';
import { DynamicForm, FormField } from 'src/components/Forms';
import { useCreatePartnerHoliday, useSelectedPartner, useUpdatePartnerHoliday } from 'src/graphql';
import { Modals, useModalMethods, useModalState } from 'src/store/modals';
import { isAfterCutoff } from './is-after-cutoff';

type FormValues = {
  title: string;
  dateRange: DateRangeValue;
};

const formFields: FormField[] = [
  {
    type: 'textfield',
    name: 'title',
    label: 'Holiday Name',
    defaultValue: '',
    variant: 'outlined',
    size: 'small',
    required: true,
    sx: {
      marginTop: '0.5rem',
      marginBottom: '0.5rem',
    },
  },
  {
    type: 'daterange',
    name: 'dateRange',
    validate: ({ from, to }) => {
      if (to.isBefore(from)) {
        return 'End date must be after start date';
      }
      return true;
    },
  },
];

export const ScheduleHolidayModal = () => {
  const { closeModal } = useModalMethods(Modals.ScheduleHolidays);
  const { isOpened, modalProps } = useModalState(Modals.ScheduleHolidays);
  const { createPartnerHoliday } = useCreatePartnerHoliday();
  const { updatePartnerHoliday } = useUpdatePartnerHoliday();
  const today = useMemo(dayjs, []);

  const { selectedPartner } = useSelectedPartner();
  const partnerTimezone = selectedPartner?.timeZone;

  const submitButtonRef = useRef<HTMLButtonElement | null>(null);
  const triggerSubmit = useCallback(() => {
    submitButtonRef?.current?.click();
  }, []);

  const onSubmit = useCallback(
    async (data: FormValues) => {
      closeModal();
      const baseHolidayData = {
        holidayName: data.title,
        holidayStart: data.dateRange.from,
        holidayEnd: data.dateRange.to,
      };
      if (modalProps?.updateExisting) {
        const { id, ...rest } = modalProps.value;
        await updatePartnerHoliday(id, {
          ...rest,
          ...baseHolidayData,
        });
      } else {
        await createPartnerHoliday({
          ...baseHolidayData,
        });
      }
    },
    [
      closeModal,
      createPartnerHoliday,
      modalProps?.updateExisting,
      modalProps?.value,
      updatePartnerHoliday,
    ],
  );

  let defaultValues: FormValues = {
    title: '',
    dateRange: { from: today.startOf('d'), to: today.endOf('d') },
  };

  if (modalProps?.updateExisting) {
    const { holidayName, holidayStart, holidayEnd } = modalProps.value;
    defaultValues = {
      title: holidayName,
      dateRange: { from: holidayStart, to: holidayEnd },
    };
  }

  const showWarning = partnerTimezone ? isAfterCutoff(partnerTimezone) : false;
  const title = modalProps?.updateExisting ? 'Update Holiday' : 'Add New Holiday';

  return (
    <Dialog open={isOpened} onClose={closeModal}>
      <DialogTitle>
        <Typography>{title}</Typography>
      </DialogTitle>
      <DialogContent>
        <DynamicForm fields={formFields} onSubmit={onSubmit} value={defaultValues}>
          <Button
            type="submit"
            ref={submitButtonRef}
            sx={{
              display: 'none',
            }}
          >
            Save
          </Button>
        </DynamicForm>
        {showWarning ? (
          <Alert severity="warning" sx={{ mt: 2 }}>
            <strong>Caution: Short-Notice Closure</strong>
            <br />
            Conversations escalated before this holiday was added may have already had expectations
            set. Please ensure appropriate communication with affected clients.
          </Alert>
        ) : null}
        <DialogActions>
          <Button onClick={closeModal}>Cancel</Button>
          <Button variant="contained" onClick={triggerSubmit}>
            Save
          </Button>
        </DialogActions>
      </DialogContent>
    </Dialog>
  );
};
